import type { GridReadyEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useRef, useState, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";

import { defaultGridOptions } from "src/config/agGrid";
import { getAllForms, getFormsRowData } from "src/reducers";
import type { AppState } from "src/types";
import { getColumns } from "./columns";
import { overlayComponentParams } from "./constants";
import FormDeletionModal from "./FormDeletionModal";
import { GridWrapper, TableContainer } from "./styles";
import type { FormDeletionModalState } from "./types";

interface Props {
  searchText: string;
}

const Table = ({ searchText }: Props): React.ReactElement => {
  const rowData = useSelector(({ app }: { app: AppState }) =>
    getFormsRowData(app),
  );
  const formsById = useSelector(({ app }: { app: AppState }) =>
    getAllForms(app),
  );
  const gridRef = useRef<AgGridReact>(null);
  const [formDeletionModal, setFormDeletionModal] =
    useState<FormDeletionModalState>({
      isOpen: false,
    });

  const columns = useMemo(
    () => getColumns(formsById, setFormDeletionModal),
    [formsById],
  );

  const onGridReady = (params: GridReadyEvent) => {
    params.api.setGridOption("quickFilterText", searchText || "");
  };

  useEffect(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.setGridOption("quickFilterText", searchText || "");
    }
  }, [searchText]);

  return (
    <>
      <TableContainer>
        <GridWrapper>
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            columnDefs={columns}
            onGridReady={onGridReady}
            animateRows={true}
            getRowId={(params) => String(params.data?.id || "")}
            overlayComponentParams={overlayComponentParams}
            {...defaultGridOptions}
          />
        </GridWrapper>
      </TableContainer>
      <FormDeletionModal
        formDeletionModal={formDeletionModal}
        setFormDeletionModal={setFormDeletionModal}
      />
    </>
  );
};

export default Table;
